import { Footer } from "@components/Footer";
import { UploaderFooter } from "@components/Footer/UploaderFooter";
import { TopNav } from "@components/TopNav";
import { useTranslation } from "@helpers/useTranslation";
import { FC, useMemo } from "react";

type Props = {
  isAdmin: boolean;
  isUploader?: boolean;
};

type AgendaSlot = {
  id: string;
  time: string;
  title: string;
};

const URBOSTOGOS_LOGO_SRC = `${process.env.PUBLIC_URL || ""}/urbostogos.png`;

/** Hard-coded Urbostogos day programme (LT) — source: sutrumpinta programa PDF. */
const EVENT_PROGRAMME_LT: AgendaSlot[] = [
  { id: "lt-1", time: "15:00 val.", title: "Urbostogų resorto atidarymas." },
  { id: "lt-2", time: "16:00 val.", title: "Urbostogų nuotykių startas." },
  { id: "lt-3", time: "16:30 val.", title: "„Žingsnių iššūkio“ apdovanojimai ir interviu (Urbostogų FM)." },
  { id: "lt-4", time: "16:40 val.", title: "Urbostogų „Crazy casino“ atidarymas." },
  { id: "lt-5", time: "16:50 val.", title: "Urbostogiški „Sonsodromo“ pojūčiai." },
  { id: "lt-6", time: "17:40 val.", title: "Urbostogų melomanų iššūkis." },
  { id: "lt-7", time: "17:50 val.", title: "Urbostogų salsos klubas." },
  { id: "lt-8", time: "19:10 val.", title: "Urbostogų vakarienė ir tradicinis sveikinimų šou." },
  { id: "lt-9", time: "20:10 val.", title: "Aukso kollegų apdovanojimai." },
  { id: "lt-10", time: "20:25 val.", title: "Urbo naujokų krikštynos." },
  { id: "lt-11", time: "20:40 val.", title: "Legendinės Urbostogų komandos apdovanojimai." },
  { id: "lt-12", time: "21:00 val.", title: "Viktoras Diawara No DJ's – lietuviškų hitų mashup." },
  { id: "lt-13", time: "22:00 val.", title: "Eurovizijos žvaigždės Lion Ceccah koncertas." },
  { id: "lt-14", time: "23:00 val.", title: "Viktoro Diawaros „No DJ's“ programa." },
  { id: "lt-15", time: "00:00 val.", title: "Urbostogų resortas užsidaro." },
];

/** Hard-coded Urbostogos day programme (EN) — translated from sutrumpinta programa PDF. */
const EVENT_PROGRAMME_EN: AgendaSlot[] = [
  { id: "en-1", time: "3:00 p.m.", title: "Opening of the Urbostogos resort." },
  { id: "en-2", time: "4:00 p.m.", title: "Start of Urbostogos adventures." },
  { id: "en-3", time: "4:30 p.m.", title: '"Steps Challenge" awards and interview (Urbostogos FM).' },
  { id: "en-4", time: "4:40 p.m.", title: 'Opening of Urbostogos "Crazy Casino".' },
  { id: "en-5", time: "4:50 p.m.", title: 'Urbostogiški "Sonsodromo" sensations.' },
  { id: "en-6", time: "5:40 p.m.", title: "Urbostogos music lovers' challenge." },
  { id: "en-7", time: "5:50 p.m.", title: "Urbostogos salsa club." },
  { id: "en-8", time: "7:10 p.m.", title: "Urbostogos dinner and traditional greetings show." },
  { id: "en-9", time: "8:10 p.m.", title: "Golden colleagues awards." },
  { id: "en-10", time: "8:25 p.m.", title: "Urbo newcomers' initiation." },
  { id: "en-11", time: "8:40 p.m.", title: "Legendary Urbostogos team awards." },
  { id: "en-12", time: "9:00 p.m.", title: "Viktoras Diawara No DJ's – Lithuanian hits mashup." },
  { id: "en-13", time: "10:00 p.m.", title: "Eurovision star Lion Ceccah concert." },
  { id: "en-14", time: "11:00 p.m.", title: 'Viktoras Diawara "No DJ\'s" programme.' },
  { id: "en-15", time: "12:00 a.m.", title: "Urbostogos resort closes." },
];

const isLithuanianUi = (lang: string): boolean => lang === "lt" || lang.startsWith("lt-");

export const Agenda: FC<Props> = ({ isAdmin, isUploader }) => {
  const { i18n } = useTranslation();

  const locale = i18n.language || "en";
  const isLt = isLithuanianUi(locale);

  const programmeHeading = isLt ? "Renginio programa" : "Event programme";

  const slots = useMemo(() => (isLt ? EVENT_PROGRAMME_LT : EVENT_PROGRAMME_EN), [isLt]);

  return (
    <div className="flex h-screen flex-col">
      <TopNav showBack isAdmin={isAdmin} />
      <main className="overflow-y-auto ">
        <div className="mx-auto mb-24 flex max-w-xl flex-col px-6 pt-6">
          <div className="mb-8 flex justify-center py-6">
            <img
              src={URBOSTOGOS_LOGO_SRC}
              alt="Urbostogos"
              className="h-auto max-h-14 w-auto max-w-[min(100%,220px)] object-contain object-center"
            />
          </div>

          <section className="mb-24">
            <h2 className="mb-8 text-center text-2xl font-semibold tracking-tight sm:text-2xl">{programmeHeading}</h2>
            <ol className="space-y-6">
              {slots.map((slot) => (
                <li key={slot.id} className="flex gap-4 text-left sm:gap-6">
                  <span className="w-[5.5rem] shrink-0 font-mono text-sm font-semibold tabular-nums text-primary sm:w-28 sm:text-base">
                    {slot.time}
                  </span>
                  <p className="min-w-0 flex-1 text-base leading-snug text-base-content">{slot.title}</p>
                </li>
              ))}
            </ol>
          </section>
        </div>
      </main>

      {isUploader && <UploaderFooter />}
      {!isAdmin && !isUploader && <Footer />}
    </div>
  );
};
